/**
 * Pupillary Distance (PD) measurement from iris landmarks.
 *
 * Uses the anatomical iris horizontal diameter (11.7 mm) as a pixel-to-mm
 * reference, then measures the inter-pupillary distance from MediaPipe iris
 * centre landmarks (468 = left, 473 = right).
 *
 * Iris ring indices:
 *   Left iris ring:  469, 470, 471, 472  (around centre 468)
 *   Right iris ring: 474, 475, 476, 477  (around centre 473)
 *
 * Human PD range: 50–80 mm (monocular 22–42 mm each side).
 * Head-pose gate: reject samples where |roll| > 5° (0.0873 rad).
 */

/** Anatomical horizontal iris diameter in millimetres. */
export const IRIS_DIAMETER_MM = 11.7;

/** Maximum head roll in radians for a valid PD sample (~5°). */
export const MAX_ROLL_RAD = 0.0873;

/** Minimum plausible PD in mm. */
export const PD_MIN_MM = 50;

/** Maximum plausible PD in mm. */
export const PD_MAX_MM = 80;

/** A single-frame PD measurement result. */
export interface PDInstantResult {
  /** Computed PD in mm for this frame. */
  pdMmInstant: number;
  /** False when the sample fails the head-pose or plausibility gate. */
  valid: boolean;
}

/** Aggregate measurement result after collecting multiple samples. */
export interface PDMeasurement {
  /** Median PD across all valid samples, in mm. */
  pdMm: number;
  /** Number of valid samples used to compute the result. */
  sampleCount: number;
  /** Quality rating based on standard deviation across samples. */
  confidence: 'low' | 'medium' | 'high';
}

/**
 * Compute the horizontal iris diameter in pixels for one iris from its
 * four boundary landmark points.
 *
 * The four ring points are assumed to be in normalised MediaPipe coords
 * [0, 1]. We multiply by `videoDim` (width for x, height for y) and then
 * take `max(x) - min(x)` as the horizontal span.
 *
 * @param ringLandmarks - The 4 ring landmarks for one iris (e.g. 469–472).
 * @param videoWidth    - Width of the video frame in pixels.
 */
export function irisHorizontalDiameterPx(
  ringLandmarks: Array<{ x: number; y: number }>,
  videoWidth: number,
): number {
  if (ringLandmarks.length === 0) return 0;
  const xs = ringLandmarks.map((lm) => lm.x * videoWidth);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  return maxX - minX;
}

/**
 * Measure PD from a single video frame's normalised landmarks.
 *
 * Applies:
 *  - Iris-diameter-referenced mm-per-pixel scale (11.7 mm anatomical reference)
 *  - Roll correction: project inter-pupil vector onto the horizontal axis
 *  - Head-pose gate: reject frames with |roll| > MAX_ROLL_RAD
 *  - Plausibility gate: reject PD outside [PD_MIN_MM, PD_MAX_MM]
 *
 * @param landmarks   - Full 478+ normalised landmark array from MediaPipe.
 * @param videoWidth  - Video frame width in pixels.
 * @param videoHeight - Video frame height in pixels.
 * @param roll        - Head roll angle in radians (from face matrix decomposition).
 * @returns Instant PD result with a validity flag.
 */
export function measurePDFromIris(
  landmarks: Array<{ x: number; y: number; z?: number }>,
  videoWidth: number,
  videoHeight: number,
  roll: number,
): PDInstantResult {
  // Head-pose gate — reject tilted head frames
  if (Math.abs(roll) > MAX_ROLL_RAD) {
    return { pdMmInstant: 0, valid: false };
  }

  // Iris ring landmarks
  const leftRing = [
    landmarks[469],
    landmarks[470],
    landmarks[471],
    landmarks[472],
  ].filter(Boolean) as Array<{ x: number; y: number }>;

  const rightRing = [
    landmarks[474],
    landmarks[475],
    landmarks[476],
    landmarks[477],
  ].filter(Boolean) as Array<{ x: number; y: number }>;

  if (leftRing.length < 2 || rightRing.length < 2) {
    return { pdMmInstant: 0, valid: false };
  }

  // Iris horizontal pixel diameters
  const leftDiamPx  = irisHorizontalDiameterPx(leftRing,  videoWidth);
  const rightDiamPx = irisHorizontalDiameterPx(rightRing, videoWidth);

  if (leftDiamPx <= 0 || rightDiamPx <= 0) {
    return { pdMmInstant: 0, valid: false };
  }

  // Average iris diameter and mm-per-pixel scale
  const irisPx   = (leftDiamPx + rightDiamPx) / 2;
  const mmPerPx  = IRIS_DIAMETER_MM / irisPx;

  // Inter-pupil pixel vector
  const leftCentre  = landmarks[468];
  const rightCentre = landmarks[473];

  if (!leftCentre || !rightCentre) {
    return { pdMmInstant: 0, valid: false };
  }

  const dxPx = (rightCentre.x - leftCentre.x) * videoWidth;
  const dyPx = (rightCentre.y - leftCentre.y) * videoHeight;

  // Project onto horizontal axis using roll correction:
  // pdPxHoriz = |d| * cos(roll) corrects for tilted head.
  const dPx        = Math.hypot(dxPx, dyPx);
  const pdPxHoriz  = dPx * Math.cos(roll);
  const pdMmInstant = pdPxHoriz * mmPerPx;

  // Plausibility gate
  if (pdMmInstant < PD_MIN_MM || pdMmInstant > PD_MAX_MM) {
    return { pdMmInstant, valid: false };
  }

  return { pdMmInstant, valid: true };
}

/**
 * Compute the median of an array of numbers.
 * Returns 0 for an empty array.
 */
export function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1]! + sorted[mid]!) / 2
    : sorted[mid]!;
}

/**
 * Compute the sample standard deviation of an array of numbers.
 * Returns 0 for arrays of length < 2.
 */
export function stddev(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
  const variance =
    values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

/**
 * Aggregate a collection of instant PD samples into a final measurement.
 *
 * Confidence thresholds:
 *  - 'high':   std dev < 1.5 mm
 *  - 'medium': std dev < 3 mm
 *  - 'low':    std dev >= 3 mm
 */
export function aggregatePDSamples(samples: number[]): PDMeasurement {
  const pdMm = median(samples);
  const sd   = stddev(samples);

  let confidence: PDMeasurement['confidence'];
  if (sd < 1.5) {
    confidence = 'high';
  } else if (sd < 3) {
    confidence = 'medium';
  } else {
    confidence = 'low';
  }

  return { pdMm, sampleCount: samples.length, confidence };
}
